import { Autocomplete, TextField } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useDebounce } from 'use-debounce'
import { Office, OfficeType, useFetchAllOfficesLazyQuery } from '../../../generated'
import { GRAPHQL_QUERY_POLICY_CACHE_FIRST } from '../../constants'
import { AutoCompleteOption } from '../../interfaceTypes/common'

const OfficeAutoComplete = ({
  currentOffice,
  name,
  label,
  sx = { paddingTop: 1 },
  required,
  disabled,
  customOnChange,
  isProfitCenter = true,
  resetKey,
  autoSelectOnTab = true
}: {
  currentOffice?: Pick<Office, 'id' | 'name'>
  name: string
  label: string
  sx?: object
  required?: boolean
  disabled?: boolean
  customOnChange?: (option: AutoCompleteOption | null) => void
  isProfitCenter?: boolean
  resetKey?: number
  autoSelectOnTab?: boolean
}) => {
  const { trigger, watch, setValue } = useFormContext()
  const [offices, setOffices] = useState<AutoCompleteOption[]>([])
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [selectedOffice, setSelectedOffice] = useState<AutoCompleteOption | null>(
    currentOffice ? { label: currentOffice.name, value: currentOffice.id } : null
  )
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500)
  const isConsultMatter = watch('isConsultMatter')
  const hasAutoSelectedRef = useRef(false)
  const stableValueRef = useRef<AutoCompleteOption | null>(null)

  const { control } = useFormContext()
  const [fetchOffices, { loading }] = useFetchAllOfficesLazyQuery({
    ...GRAPHQL_QUERY_POLICY_CACHE_FIRST,
    variables: undefined,
    onCompleted(data) {
      if (data?.fetchAllOffices?.offices?.length) {
        const newOffices = data.fetchAllOffices.offices
          ?.filter(office => (isProfitCenter ? office?.type === OfficeType.ProfitCenter : true))
          ?.map(office => ({
            value: office?.id || 0,
            label: office?.name || ''
          }))
        setOffices(newOffices)
      }
    }
  })

  useEffect(() => {
    fetchOffices({
      variables: {
        fetchOfficesInput: {
          keyword: debouncedSearchTerm
        }
      }
    })
  }, [debouncedSearchTerm, fetchOffices])

    useEffect(() => {
      if (isConsultMatter && !selectedOffice && !hasAutoSelectedRef.current) {
        const defaultOffice = offices.find(o => o.label === 'Administrative')
        if (defaultOffice) {
          hasAutoSelectedRef.current = true
          setSelectedOffice(defaultOffice)
          setValue(name, {
            id: defaultOffice.value,
            name: defaultOffice.label
          })
        }
      }
    }, [offices, isConsultMatter])

  useEffect(() => {
    if (resetKey) {
      setSelectedOffice(null)
    }
  }, [resetKey])

  // Follow `currentOffice` when the CALLER changes it, instead of only reading it at mount — a URL-driven
  // filter (or a reconciliation session restoring its saved view) can clear or change the selection from
  // outside, and the box would otherwise keep showing an office that is no longer being filtered on.
  // Returning `prev` unchanged keeps this a no-op for callers whose currentOffice never moves.
  useEffect(() => {
    setSelectedOffice(prev => {
      const next = currentOffice ? { label: currentOffice.name, value: currentOffice.id } : null
      return prev?.value === next?.value && prev?.label === next?.label ? prev : next
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOffice?.id, currentOffice?.name])


  const handleOfficeChange = (option: AutoCompleteOption | null) => {
    setSelectedOffice(option)
    customOnChange?.(option)
  }

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error: fieldError } }) => {
        const { value } = field
        const error = fieldError as unknown as {
          message?: string
          value?: { message: string }
          id?: { message: string }
        }
        const errorMessage = error?.message || error?.value?.message || error?.id?.message || ''

        // Resolve the current selection against the loaded options. The second lookup matters when the caller
        // knows only the office ID — a filter restored from the URL, say — so the label it handed us is not a
        // real office name; matching on the id replaces it with the proper one as soon as the list arrives.
        const nextValue =
          offices.find(o => o.value === value?.value) ??
          offices.find(o => o.value === selectedOffice?.value) ??
          selectedOffice ??
          null
        // Hand MUI the same object while the selection itself is unchanged. Every search rebuilds `offices`, so
        // the lookup above returns a new object each time, and MUI treats a new value object as a new selection
        // and writes its name back into the box — wiping out whatever the user was typing.
        const previousValue = stableValueRef.current
        const resolvedValue =
          nextValue && previousValue?.value === nextValue.value && previousValue?.label === nextValue.label
            ? previousValue
            : nextValue
        stableValueRef.current = resolvedValue

        return (
          <Autocomplete
            sx={sx}
            options={offices}
            value={resolvedValue}
            loading={loading}
            disabled={disabled}
            autoHighlight={autoSelectOnTab}
            autoSelect={autoSelectOnTab}
            onChange={(_, value) => {
              field.onChange(value)
              handleOfficeChange(value)
              return trigger(name)
            }}
            // Search only on what the user types. MUI also fires this with reason 'reset' to write the selected
            // office's name into the box; searching on that narrowed the list to the current office alone (a
            // consult on "Administrative" offered only "Administrative"), so the field looked impossible to change.
            onInputChange={(_, newInputValue, reason) => setSearchTerm(reason === 'input' ? newInputValue : '')}
            getOptionLabel={option => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            renderInput={params => (
              <TextField
                {...params}
                label={label}
                required={required}
                error={!!errorMessage}
                helperText={errorMessage}
              />
            )}
            ListboxProps={{
              role: 'list-box',
              style: {
                maxHeight: 230,
                overflowY: 'auto'
              }
            }}
            noOptionsText={loading ? 'Loading...' : 'No Offices!'}
          />
        )
      }}
    />
  )
}

export default OfficeAutoComplete
